import { Row } from 'antd';
import ChatSupport from 'components/ChatSupport';
import DownloadApp from 'components/DownloadApp';
import { ErrorBoundary } from 'components/ErrorBoundary';
import React, { lazy, Suspense, useMemo } from 'react';
const Select = lazy(() => import('components/Select/Select'));
import { FormattedMessage } from 'react-intl';
import { Link, RouteProps } from 'react-router-dom';
import styled from 'styled-components';
import { Language } from 'types/schema';
import media from 'utils/mediaStyle';
import messages from 'utils/messages';
import { setLang } from 'utils/preloadHintCookie';
import { clearReferralParams, hasReferralParam } from 'utils/referralUtils';
import SVGIcon from '../SVGIcon/SVGIcon';
import BackIcon from './Back.svg';
import bg1 from './Slice-1.png';
import bg2 from './Slice-2.png';
import bg3 from './Slice-3.png';
import bg from './Slice.png';

const Container = styled.div`
  background-color: #fff;
  width: 100%;
  height: 100%;
  position: relative;
`;
const Content = styled.div<{ isEmptyLayout?: boolean; $fullscreenOnMobile?: boolean }>`
  width: 100%;
  height: ${({ isEmptyLayout }) => (isEmptyLayout ? '100vh' : '100%')};
  position: relative;
  z-index: 1;
  padding: 80pt 8pt;
  ${({ isEmptyLayout }) =>
    !isEmptyLayout &&
    media.lg`
    padding: 100pt 8pt;
  `};
  ${({ isEmptyLayout }) =>
    isEmptyLayout &&
    `
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  `}
  ${({ $fullscreenOnMobile }) =>
    $fullscreenOnMobile &&
    `
    @media (max-width: 576px) {
      padding: 0;
    }
  `}
`;
const Bg = styled.div<{ $fullscreenOnMobile?: boolean }>`
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 0;
  top: 0;
  left: 0;
  background-color: #f9fafc;
  ${({ $fullscreenOnMobile }) =>
    $fullscreenOnMobile &&
    `
    @media (max-width: 576px) {
      display: none;
    }
  `}
`;

const ImageTopLeft = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  max-width: 731px;
  width: 50.7%;
`;
const ImageTopRight = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  max-width: 709px;
  width: 49.3%;
`;
const ImageBottomLeft = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  max-width: 955px;
  width: 66.4%;
`;
const ImageBottomRight = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  max-width: 485px;
  width: 33.6%;
`;
const LangContainer = styled.div<{ $fullscreenOnMobile?: boolean }>`
  position: absolute;
  top: 32px;
  right: 32px;
  ${({ $fullscreenOnMobile }) =>
    $fullscreenOnMobile &&
    `
    @media (max-width: 576px) {
      display: none;
    }
  `}
`;

const DownloadAppContainer = styled.div<{ $fullscreenOnMobile?: boolean }>`
  margin-top: 40px;
  ${({ $fullscreenOnMobile }) =>
    $fullscreenOnMobile &&
    `
    @media (max-width: 576px) {
      display: none;
    }
  `}
`;
const BackBtn = styled.div<{ $fullscreenOnMobile?: boolean }>`
  ${({ $fullscreenOnMobile }) =>
    $fullscreenOnMobile &&
    `
    @media (max-width: 576px) {
      display: none;
    }
  `}
  max-width: 720px;
  margin: 0 auto;
  margin-bottom: 10px;

  & a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    & > div {
      width: 35px;
      height: 35px;
      background: #f5f5f5;
      border-radius: 50%;
      cursor: pointer;
      & > div {
        justify-content: unset;
      }
    }
    & > span {
      color: rgb(51, 51, 51);
    }
    &:hover {
      span {
        color: rgb(67, 160, 71);
      }
    }
  }
`;
interface GateLayoutProps extends RouteProps {
  children?: JSX.Element[] | JSX.Element;
  size?: string;
}

type IEmptyLayoutPaths = {
  path: string;
  params: string[];
}[];

const emptyLayoutPaths: IEmptyLayoutPaths = [
  {
    path: '/kamereo-internal-signup',
    params: ['createdBy'],
  },
];

const GateLayout = (props: GateLayoutProps) => {
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const lang = window.localStorage.getItem('lang') || 'vi';
  const store = window.localStorage.getItem('store') || '';

  const onChangeLang = (value: Language) => {
    setLang(value);
    location.reload();
  };

  const isEmptyLayout = useMemo(() => {
    const emptyLayoutPath = emptyLayoutPaths.find((item) => item.path === location.pathname);
    if (emptyLayoutPath) {
      return emptyLayoutPath.params.some((param) => !searchParams.has(param));
    }
    return false;
  }, [location.pathname, searchParams]);

  const isMobileFullscreenPage = location.pathname === '/forgot-password';

  return (
    <Container>
      <Content isEmptyLayout={isEmptyLayout} $fullscreenOnMobile={isMobileFullscreenPage}>
        <LangContainer $fullscreenOnMobile={isMobileFullscreenPage}>
          <Suspense fallback={<span style={{ display: 'inline-block', minWidth: 60, height: 24 }} />}>
            <Select
              onChange={onChangeLang}
              size="small"
              value={lang}
              items={[
                { value: 'en', label: 'EN' },
                { value: 'vi', label: 'VI' },
              ]}
              style={{ minWidth: 60 }}
            />
          </Suspense>
        </LangContainer>
        {!isEmptyLayout &&
          ((store && location.pathname.includes('/change-store')) ||
            (!store && !location.pathname.includes('/change-store'))) && (
            <BackBtn $fullscreenOnMobile={isMobileFullscreenPage}>
              <Link
                to="/"
                onClick={() => {
                  const isHasReferralParam = hasReferralParam('ODOO_CUSTOMER_ID');
                  if (isHasReferralParam) {
                    clearReferralParams();
                  }
                }}
              >
                <SVGIcon src={BackIcon} />
                <FormattedMessage {...messages.backToHome} />
              </Link>
            </BackBtn>
          )}

        <Row justify="center" align="top">
          <ErrorBoundary>{props.children}</ErrorBoundary>
        </Row>
        {!isEmptyLayout && (
          <DownloadAppContainer $fullscreenOnMobile={isMobileFullscreenPage}>
            <DownloadApp />
          </DownloadAppContainer>
        )}
      </Content>
      <ChatSupport />
      <Bg $fullscreenOnMobile={isMobileFullscreenPage}>
        <ImageTopLeft src={bg1} />
        <ImageTopRight src={bg2} />
        <ImageBottomLeft src={bg} />
        <ImageBottomRight src={bg3} />
      </Bg>
    </Container>
  );
};

export default GateLayout;
